import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Modal,
  Form,
  Button,
  Alert,
  ProgressBar,
  Tabs,
  Tab,
  Table,
  Badge
} from 'react-bootstrap';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { athleteAccessService, emailTemplateService } from '../../services/api';
import { isMinor } from '../../utils/userHelpers';

const DEFAULT_PORTAL_BODY = `<p>Ciao <strong>{nome} {cognome}</strong>,</p>
<p>È stato generato il link di accesso per il tuo portale personale. Clicca sul pulsante qui sotto per accedere e gestire la tua anagrafica:</p>
<div style="text-align: center; margin: 30px 0;">
    <a href="{link}" style="background-color: #0d6efd; color: white; padding: 12px 30px; text-decoration: none; border-radius: 5px; display: inline-block; font-weight: bold;">
        Accedi al Portale
    </a>
</div>
<p style="font-size: 14px; color: #666;">Questo link è valido per l'accesso.</p>
<p style="font-size: 14px; color: #666;">Se il pulsante non funziona, copia questo indirizzo nel tuo browser: {link}</p>`;

const DEFAULT_PORTAL_SUBJECT = 'Accesso al Portale Atleti - {fromName}';

const SendBulkAthleteAccessModal = ({
  show,
  onHide,
  selectedUsers = [],
  onSuccess,
  onError
}) => {
  const [formData, setFormData] = useState({
    subject: DEFAULT_PORTAL_SUBJECT,
    body: DEFAULT_PORTAL_BODY
  });
  const [loading, setLoading] = useState(false);
  const [sendingProgress, setSendingProgress] = useState(0);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('compose');
  const quillRef = useRef(null);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  // Resolves email recipient for a user
  const resolveRecipient = useCallback((user) => {
    if (!user) return { email: null, type: 'none', label: 'Nessuna email' };

    const minor = isMinor(user.dateOfBirth, user.fiscalCode);

    if (minor) {
      if (user.parent?.email && user.parent.email.trim()) {
        const parentName = [user.parent.firstName, user.parent.lastName].filter(Boolean).join(' ');
        return {
          email: user.parent.email.trim(),
          type: 'parent',
          label: parentName ? `Genitore (${parentName})` : 'Genitore'
        };
      }
      const athleteEmail = (user.loginEmail && user.loginEmail.trim()) || (user.email && user.email.trim());
      if (athleteEmail) {
        return {
          email: athleteEmail,
          type: 'minor_athlete_fallback',
          label: 'Atleta (fallback: genitore senza email)'
        };
      }
      return {
        email: null,
        type: 'none',
        label: 'Genitore e atleta senza email'
      };
    }

    if (user.loginEmail && user.loginEmail.trim()) {
      return {
        email: user.loginEmail.trim(),
        type: 'login',
        label: 'Email login portale'
      };
    }

    if (user.email && user.email.trim()) {
      return {
        email: user.email.trim(),
        type: 'user',
        label: 'Email atleta'
      };
    }

    return { email: null, type: 'none', label: 'Nessuna email' };
  }, []);

  // Analyzed recipients list
  const analyzedRecipients = useMemo(() => {
    return selectedUsers.map(user => {
      const recipient = resolveRecipient(user);
      return {
        user,
        email: recipient.email,
        type: recipient.type,
        label: recipient.label,
        isValid: Boolean(recipient.email)
      };
    });
  }, [selectedUsers, resolveRecipient]);

  const validRecipients = useMemo(() => {
    return analyzedRecipients.filter(r => r.isValid);
  }, [analyzedRecipients]);

  const missingRecipients = useMemo(() => {
    return analyzedRecipients.filter(r => !r.isValid);
  }, [analyzedRecipients]);

  // Available placeholders
  const placeholders = useMemo(() => [
    { tag: '{nome}', label: 'Nome atleta', preview: 'Mario' },
    { tag: '{cognome}', label: 'Cognome atleta', preview: 'Rossi' },
    { tag: '{link}', label: 'Link accesso portale', preview: `${window.location.origin}/portal/setup/token-demo` },
    { tag: '{fromName}', label: 'Nome associazione', preview: 'Sportus' }
  ], []);

  // Check if body has link placeholder
  const hasLinkPlaceholder = useMemo(() => {
    if (!formData.body) return false;
    const lower = formData.body.toLowerCase();
    return lower.includes('{link}') || lower.includes('{url}') || lower.includes('{accessurl}') || lower.includes('{invitationlink}');
  }, [formData.body]);

  // Insert placeholder into Quill editor
  const insertPlaceholder = useCallback((tag) => {
    const editor = quillRef.current?.getEditor();
    if (editor) {
      const range = editor.getSelection(true);
      editor.insertText(range.index, tag);
      editor.setSelection(range.index + tag.length);
    } else {
      setFormData(prev => ({
        ...prev,
        body: prev.body.replace('</p>', `${tag}</p>`)
      }));
    }
  }, []);

  // Replace placeholders for preview
  const replacePreviewPlaceholders = useCallback((text) => {
    if (!text) return text;
    let result = text;
    placeholders.forEach(p => {
      result = result.replaceAll(p.tag, p.preview);
    });
    return result;
  }, [placeholders]);

  // Reset and load templates when opening
  useEffect(() => {
    if (show) {
      setError('');
      setSendingProgress(0);
      setActiveTab('compose');

      emailTemplateService.getEmailTemplates().then(result => {
        if (result.success && Array.isArray(result.data)) {
          setEmailTemplates(result.data);
          const portalTemplate = result.data.find(t => t.name === 'accesso-atleta' || t.name === 'Accesso Atleta');
          if (portalTemplate) {
            setSelectedTemplateId(portalTemplate.id);
            setFormData({
              subject: portalTemplate.subject || DEFAULT_PORTAL_SUBJECT,
              body: portalTemplate.body || DEFAULT_PORTAL_BODY
            });
          } else {
            setSelectedTemplateId('');
            setFormData({
              subject: DEFAULT_PORTAL_SUBJECT,
              body: DEFAULT_PORTAL_BODY
            });
          }
        }
      }).catch(() => {
        setFormData({
          subject: DEFAULT_PORTAL_SUBJECT,
          body: DEFAULT_PORTAL_BODY
        });
      });
    }
  }, [show]);

  const handleChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLoadTemplate = (templateId) => {
    setSelectedTemplateId(templateId);
    if (!templateId) {
      setFormData({
        subject: DEFAULT_PORTAL_SUBJECT,
        body: DEFAULT_PORTAL_BODY
      });
      return;
    }
    const template = emailTemplates.find(t => t.id === templateId);
    if (template) {
      setFormData({
        subject: template.subject,
        body: template.body
      });
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError('');
      setSendingProgress(0);
      setActiveTab('compose');
      onHide();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.subject.trim() || !formData.body.trim()) {
      setError('Compilare oggetto e corpo dell\'email');
      return;
    }

    if (validRecipients.length === 0) {
      setError('Nessun destinatario valido con indirizzo email selezionato');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSendingProgress(0);

      // Simulated progress bar during backend execution
      const progressInterval = setInterval(() => {
        setSendingProgress(prev => {
          if (prev >= 90) return prev;
          return prev + 10;
        });
      }, 250);

      const recipientUserIds = validRecipients.map(r => r.user.id);
      const response = await athleteAccessService.sendBulkAthleteAccessLinks(
        recipientUserIds,
        formData.subject,
        formData.body
      );

      clearInterval(progressInterval);
      setSendingProgress(100);

      if (response.success) {
        const data = response.data;
        let message = '';
        if (data.totalFailed === 0 && data.totalSkipped === 0) {
          message = `✅ ${data.totalSent} link di accesso inviati con successo!`;
        } else {
          message = `⚠️ Inviati: ${data.totalSent}, Falliti: ${data.totalFailed}, Saltati (senza email): ${data.totalSkipped + missingRecipients.length}.`;
        }

        if (onSuccess) {
          onSuccess(message);
        }

        setTimeout(() => {
          handleClose();
        }, 1200);
      } else {
        setError(response.message || 'Errore durante l\'invio dei link di accesso');
        if (onError) {
          onError(response.message);
        }
      }
    } catch (err) {
      setError('Errore di connessione al server');
      console.error('Error sending bulk athlete access links:', err);
      if (onError) {
        onError('Errore di connessione durante l\'invio');
      }
    } finally {
      setLoading(false);
    }
  };

  const quillModules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['link'],
      ['clean']
    ]
  }), []);

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'color', 'background',
    'align',
    'link'
  ];

  return (
    <Modal show={show} onHide={handleClose} size="lg" backdrop="static">
      <Modal.Header closeButton={!loading}>
        <Modal.Title>
          <i className="fas fa-key me-2 text-primary"></i>
          Invia Link di Accesso Portale ({selectedUsers.length} atleti selezionati)
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger" onClose={() => setError('')} dismissible>
              {error}
            </Alert>
          )}

          {/* Missing email alert */}
          {missingRecipients.length > 0 && (
            <Alert variant="warning" className="mb-3">
              <i className="fas fa-exclamation-triangle me-2"></i>
              <strong>Attenzione:</strong> {missingRecipients.length} {missingRecipients.length === 1 ? 'atleta selezionato non ha' : 'atleti selezionati non hanno'} un indirizzo email configurato e {missingRecipients.length === 1 ? 'verrà escluso' : 'verranno esclusi'} dall'invio.
              <span className="ms-2">
                (Destinatari pronti per l'invio: <strong>{validRecipients.length}</strong> su {selectedUsers.length})
              </span>
            </Alert>
          )}

          {/* Progress bar when sending */}
          {loading && (
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span>Invio dei link di accesso in corso...</span>
                <span>{sendingProgress}%</span>
              </div>
              <ProgressBar
                now={sendingProgress}
                variant={sendingProgress === 100 ? 'success' : 'primary'}
                animated={sendingProgress < 100}
              />
            </div>
          )}

          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="mb-3"
          >
            {/* Tab Componi */}
            <Tab eventKey="compose" title="Componi Email">
              {emailTemplates.length > 0 && (
                <Form.Group className="mb-3">
                  <Form.Label>Carica da template pre-impostato</Form.Label>
                  <Form.Select
                    value={selectedTemplateId}
                    onChange={(e) => handleLoadTemplate(e.target.value)}
                    disabled={loading}
                  >
                    <option value="">— Template predefinito accesso portale —</option>
                    {emailTemplates.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Seleziona un template per caricare l'oggetto e il corpo del messaggio.
                  </Form.Text>
                </Form.Group>
              )}

              <Form.Group className="mb-3">
                <Form.Label>Oggetto *</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Oggetto dell'email"
                  value={formData.subject}
                  onChange={(e) => handleChange('subject', e.target.value)}
                  disabled={loading}
                  required
                />
              </Form.Group>

              {!hasLinkPlaceholder && (
                <Alert variant="warning" className="py-2 mb-3">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  <strong>Nota:</strong> Il segnaposto <code>{'{link}'}</code> non è presente nel corpo dell'email. Ricordati di includerlo per permettere agli atleti di accedere al portale.
                </Alert>
              )}

              <Form.Group className="mb-3">
                <Form.Label>Corpo Email (HTML) *</Form.Label>
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  value={formData.body}
                  onChange={(value) => handleChange('body', value)}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Scrivi il contenuto dell'email..."
                  readOnly={loading}
                  style={{ height: '260px', marginBottom: '50px' }}
                />
              </Form.Group>

              <Alert variant="info" className="mt-3">
                <i className="fas fa-tags me-2"></i>
                <strong>Variabili disponibili:</strong> clicca per inserire nel corpo dell'email.
                <div className="mt-2 d-flex gap-2 flex-wrap">
                  {placeholders.map(p => (
                    <Button
                      key={p.tag}
                      variant="outline-primary"
                      size="sm"
                      disabled={loading}
                      onClick={() => insertPlaceholder(p.tag)}
                    >
                      <i className="fas fa-plus-circle me-1"></i>
                      {p.tag} <span className="text-muted">({p.label})</span>
                    </Button>
                  ))}
                </div>
              </Alert>
            </Tab>

            {/* Tab Anteprima */}
            <Tab eventKey="preview" title="Anteprima">
              <Alert variant="secondary" className="mb-3">
                <i className="fas fa-eye me-2"></i>
                I segnaposto vengono sostituiti con dati di esempio (Mario Rossi).
              </Alert>
              <div className="border rounded p-3 bg-light">
                <div className="mb-3">
                  <strong>Oggetto:</strong>
                  <div className="mt-1">
                    {formData.subject
                      ? replacePreviewPlaceholders(formData.subject)
                      : <em className="text-muted">Nessun oggetto</em>}
                  </div>
                </div>
                <hr />
                <div>
                  <strong>Corpo:</strong>
                  <div
                    className="mt-2 p-3 bg-white border rounded"
                    style={{ minHeight: '200px', maxHeight: '400px', overflow: 'auto' }}
                    dangerouslySetInnerHTML={{
                      __html: replacePreviewPlaceholders(formData.body) || '<em class="text-muted">Nessun contenuto</em>'
                    }}
                  />
                </div>
              </div>
            </Tab>

            {/* Tab Destinatari */}
            <Tab eventKey="recipients" title={`Destinatari (${selectedUsers.length})`}>
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <Table responsive hover size="sm">
                  <thead>
                    <tr>
                      <th>Atleta</th>
                      <th>Email di Destinazione</th>
                      <th>Tipo</th>
                      <th>Stato</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analyzedRecipients.map(item => (
                      <tr key={item.user.id} className={item.isValid ? '' : 'table-warning'}>
                        <td>
                          <strong>{item.user.firstName} {item.user.lastName}</strong>
                          {item.user.fiscalCode && (
                            <small className="text-muted d-block">{item.user.fiscalCode}</small>
                          )}
                        </td>
                        <td>
                          {item.email ? (
                            <span className="font-monospace">{item.email}</span>
                          ) : (
                            <span className="text-danger fst-italic">Mancante</span>
                          )}
                        </td>
                        <td>
                          {item.type === 'minor_athlete_fallback' ? (
                            <Badge bg="warning" text="dark" className="d-inline-flex align-items-center">
                              <i className="fas fa-exclamation-triangle me-1"></i>
                              {item.label}
                            </Badge>
                          ) : (
                            <small className="text-muted">{item.label}</small>
                          )}
                        </td>
                        <td>
                          {item.isValid ? (
                            <Badge bg="success">
                              <i className="fas fa-check me-1"></i>
                              Pronto
                            </Badge>
                          ) : (
                            <Badge bg="warning" text="dark">
                              <i className="fas fa-times me-1"></i>
                              Escluso (Senza Email)
                            </Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Tab>
          </Tabs>
        </Modal.Body>

        <Modal.Footer className="d-flex justify-content-between">
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={loading}
          >
            Annulla
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={loading || validRecipients.length === 0 || !formData.subject.trim() || !formData.body.trim()}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Invio in corso...
              </>
            ) : (
              <>
                <i className="fas fa-paper-plane me-2"></i>
                Invia Link a {validRecipients.length} {validRecipients.length === 1 ? 'Atleta' : 'Atleti'}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default SendBulkAthleteAccessModal;
